// Sprint 6 pendiente: leer chef_sla_minutos_1 y chef_reversion_minutos de
// /api/v1/config/public (endpoint expuesto en Sprint 5, whitelist incluye
// las keys). Por ahora se usan los defaults ADR-011 v2.0. Ver
// docs/TODOS_PENDIENTES_SPRINT6.md #3.
import { useEffect, useMemo, useState } from 'react'
import AppHeader from '../components/AppHeader'
import F11Card from '../components/F11Card'
import AprobarF11Modal from '../components/AprobarF11Modal'
import RechazarF11Modal from '../components/RechazarF11Modal'
import RevertirF11Banner from '../components/RevertirF11Banner'
import { useAuth } from '../hooks/useAuth'
import { useJefeCocinaStore } from '../stores/jefeCocinaStore'
import { useToast } from '../components/Toast'
import type {
  KdsCard,
  SolicitudCambioListItem,
} from '../services/jefeCocina'

// Defaults ADR-011 v2.0 (chef_sla_minutos_1=15, chef_reversion_minutos=5).
// Cuando se exponga endpoint de config, sustituir lectura via hook.
const CHEF_SLA_MIN_DEFAULT = 15
const CHEF_REVERSION_MIN_DEFAULT = 5

// Tanda 6: se conserva solo la key para limpiar el estado heredado del toggle
// obsoleto (ver el useEffect que la elimina). readDarkMode se retiró.
const DARK_MODE_KEY = 'avante_jefe_cocina_dark'

const KDS_ESTADO_COLOR: Record<string, string> = {
  OFERTA_CONFIRMADA: 'bg-blue-100 text-blue-900 dark:bg-blue-900/60 dark:text-blue-100',
  EN_PREPARACION: 'bg-orange-100 text-orange-900 dark:bg-orange-900/60 dark:text-orange-100',
  LISTA: 'bg-green-100 text-green-900 dark:bg-green-900/60 dark:text-green-100',
  CAMBIO_SOLICITADO: 'bg-yellow-100 text-yellow-900 dark:bg-yellow-900/60 dark:text-yellow-100',
}

export default function JefeCocinaDashboard() {
  const { user, logout } = useAuth()
  const { showToast } = useToast()
  const {
    pendientes,
    kds,
    loading,
    error,
    ultimaDecision,
    refrescar,
    aprobar,
    rechazar,
    revertir,
    limpiarUltimaDecision,
  } = useJefeCocinaStore()

  const [solicitudAprobar, setSolicitudAprobar] = useState<SolicitudCambioListItem | null>(null)
  const [solicitudRechazar, setSolicitudRechazar] = useState<SolicitudCambioListItem | null>(null)
  useEffect(() => {
    refrescar()
    const id = window.setInterval(() => {
      refrescar()
    }, 15_000)
    return () => window.clearInterval(id)
  }, [refrescar])

  // Tanda 6 (5-jul): el toggle Claro/Oscuro quedó obsoleto (toda la app es
  // oscura vía index.css) y además fugaba la clase `dark` a <html> afectando
  // otras pantallas. Se retira y se limpia cualquier estado heredado.
  useEffect(() => {
    document.documentElement.classList.remove('dark')
    window.localStorage.removeItem(DARK_MODE_KEY)
  }, [])

  useEffect(() => {
    if (error) showToast(error, 'error')
  }, [error, showToast])

  const escaladas = useMemo(
    () => kds.filter((c) => c.estado === 'CAMBIO_SOLICITADO' && c.tiene_solicitud_pendiente),
    [kds],
  )

  async function handleConfirmAprobar(body: Parameters<typeof aprobar>[1]) {
    if (!solicitudAprobar) return
    const id = solicitudAprobar.id
    try {
      await aprobar(id, body)
      showToast(`Solicitud #${id} aprobada`, 'success')
      setSolicitudAprobar(null)
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error al aprobar'
      showToast(msg, 'error')
      throw err
    }
  }

  async function handleConfirmRechazar(body: Parameters<typeof rechazar>[1]) {
    if (!solicitudRechazar) return
    const id = solicitudRechazar.id
    try {
      await rechazar(id, body)
      showToast(`Solicitud #${id} rechazada`, 'success')
      setSolicitudRechazar(null)
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error al rechazar'
      showToast(msg, 'error')
      throw err
    }
  }

  async function handleRevertir(id: number) {
    try {
      await revertir(id)
      showToast(`Solicitud #${id} revertida — vuelve a pendiente`, 'success')
      limpiarUltimaDecision()
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'No se pudo revertir'
      showToast(msg, 'error')
      throw err
    }
  }

  const sedeLabel = user?.sede_id ? `Sede #${user.sede_id}` : 'Todas las sedes'
  const pendientesCount = pendientes.length

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-gray-50">
      <AppHeader title="Jefe de Cocina — F11" user={user} onLogout={logout}>
        <div className="flex items-center gap-3 text-sm">
          <span aria-label="Sede asignada">{sedeLabel}</span>
          <span
            aria-label={`${pendientesCount} solicitudes pendientes`}
            className={`inline-flex items-center justify-center min-w-[1.75rem] h-7 px-2 rounded-full text-xs font-bold ${
              pendientesCount > 0
                ? 'bg-avante-danger text-white animate-pulse'
                : 'bg-white/20 text-white'
            }`}
          >
            {pendientesCount}
          </span>
        </div>
      </AppHeader>

      {ultimaDecision && (
        <RevertirF11Banner
          decision={ultimaDecision}
          ventanaMinutos={CHEF_REVERSION_MIN_DEFAULT}
          onRevertir={handleRevertir}
          onDismiss={limpiarUltimaDecision}
        />
      )}

      <main className="p-4 space-y-6 max-w-6xl mx-auto">
        <section aria-labelledby="pendientes-heading">
          <div className="flex items-baseline justify-between mb-2">
            <h2
              id="pendientes-heading"
              className="text-lg font-bold text-gray-900 dark:text-gray-50"
            >
              Pendientes mi aprobacion
            </h2>
            {loading && pendientes.length === 0 && (
              <span className="text-xs text-gray-500">Cargando…</span>
            )}
          </div>

          {pendientes.length === 0 && !loading && (
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 text-center text-gray-500 dark:text-gray-400">
              Sin solicitudes pendientes.
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {pendientes.map((s) => (
              <F11Card
                key={s.id}
                solicitud={s}
                slaMinutos={CHEF_SLA_MIN_DEFAULT}
                onAprobar={setSolicitudAprobar}
                onRechazar={setSolicitudRechazar}
              />
            ))}
          </div>
        </section>

        <section aria-labelledby="kds-heading">
          <h2
            id="kds-heading"
            className="text-lg font-bold text-gray-900 dark:text-gray-50 mb-2"
          >
            KDS resumen — {sedeLabel}
          </h2>
          {kds.length === 0 && !loading && (
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 text-center text-gray-500 dark:text-gray-400">
              Sin comidas activas.
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {kds.map((c) => (
              <KdsResumenCard key={c.comida_id} card={c} />
            ))}
          </div>
        </section>

        <section aria-labelledby="escaladas-heading">
          <h2
            id="escaladas-heading"
            className="text-lg font-bold text-gray-900 dark:text-gray-50 mb-2"
          >
            Escalaciones recientes
          </h2>
          {escaladas.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Sin escalaciones activas en KDS.
            </p>
          ) : (
            <ul className="space-y-1">
              {escaladas.map((c) => (
                <li
                  key={c.comida_id}
                  className="text-sm bg-white dark:bg-gray-800 rounded px-3 py-2"
                >
                  Hab. {c.habitacion} — {c.paciente_nombre} ({c.dieta_nombre})
                  {c.solicitud_id != null && ` · sol #${c.solicitud_id}`}
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>

      {solicitudAprobar && (
        <AprobarF11Modal
          solicitud={solicitudAprobar}
          onConfirm={handleConfirmAprobar}
          onCancel={() => setSolicitudAprobar(null)}
        />
      )}
      {solicitudRechazar && (
        <RechazarF11Modal
          solicitud={solicitudRechazar}
          onConfirm={handleConfirmRechazar}
          onCancel={() => setSolicitudRechazar(null)}
        />
      )}
    </div>
  )
}

function KdsResumenCard({ card }: { card: KdsCard }) {
  const colorClass = KDS_ESTADO_COLOR[card.estado] ?? 'bg-gray-100 text-gray-900'
  return (
    <article className="bg-white dark:bg-gray-800 rounded-lg shadow p-3 text-sm border-l-4 border-avante-accent">
      <p className="font-bold">
        Hab. {card.habitacion} — {card.paciente_nombre}
      </p>
      <p className="text-gray-700 dark:text-gray-300">
        {card.dieta_nombre} · {card.tiempo_nombre}
      </p>
      <div className="mt-2 flex items-center gap-2">
        <span className={`text-xs px-2 py-0.5 rounded font-semibold ${colorClass}`}>
          {card.estado}
        </span>
        {card.tiene_solicitud_pendiente && (
          <span className="text-xs px-2 py-0.5 rounded bg-avante-warning text-black font-semibold">
            F11 #{card.solicitud_id}
          </span>
        )}
      </div>
    </article>
  )
}
